import Select from "@components/common/Select";
import { Button, Checkbox, Datepicker, Input } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import theme from "@theme";
import { Card } from "@services/travel";
import { CardType } from "@gen/travel/v1/catalog_pb";
import { useEffect } from "react";

interface CardValues {
  type: string;
  number: string;
  effectiveDate: string;
  expirationDate: string;
  consentCheck: boolean;
  privacyCheck: boolean;
}

interface CardFormProps {
  card?: Card;
  cardTypes: CardType[];
  onSubmit: (values: CardValues) => void;
  onClose: () => void;
}

export function CardForm({
  card,
  cardTypes,
  onSubmit,
  onClose,
}: CardFormProps) {
  const { t } = useTranslation();
  const { control, handleSubmit, reset, watch } = useForm<CardValues>({
    defaultValues: {
      type: "",
      number: "",
      effectiveDate: "",
      expirationDate: "",
      consentCheck: false,
      privacyCheck: false,
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const watchedValues = watch();

  const isFormFilled =
    watchedValues.type !== "" &&
    watchedValues.number !== "" &&
    watchedValues.effectiveDate !== "" &&
    watchedValues.expirationDate !== "" &&
    watchedValues.consentCheck === true &&
    watchedValues.privacyCheck === true;

  function dateToString(date: Date | undefined): string | undefined {
    if (!date) {
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function stringToDate(dateString: string | undefined): Date | undefined {
    if (!dateString) {
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return;
    }
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date;
  }

  useEffect(() => {
    if (card) {
      reset({
        type: card.type,
        number: card.number,
        effectiveDate: card.effectiveDate,
        expirationDate: card.expirationDate,
        consentCheck: true,
        privacyCheck: true,
      });
    }
  }, [card, reset]);

  const onFormSubmit = handleSubmit((data) => {
    onSubmit(data);
    onClose();
  });

  return (
    <div className="grid grid-cols-2 gap-6 max-w-[700px]">
      <Controller
        control={control}
        name="type"
        rules={{
          required: t("search.passenger.card.type.required"),
        }}
        render={({ field, fieldState }) => (
          <Select
            label={t("search.passenger.card.type.label")}
            value={field.value}
            onChange={field.onChange}
            search
            options={cardTypes.map((c) => ({
              label: c.name,
              value: c.code,
            }))}
            error={fieldState.invalid}
            errorMsg={fieldState.error?.message}
          />
        )}
      />
      <div>
        <Controller
          control={control}
          name="number"
          rules={{
            required: t("search.passenger.card.number.required"),
          }}
          render={({ field, fieldState }) => (
            <Input
              type="text"
              label={t("search.passenger.card.number.label")}
              placeholder={t("search.passenger.card.number.label")}
              value={field.value as string}
              onChange={field.onChange}
              error={fieldState.invalid}
              errorMsg={fieldState.error?.message}
            />
          )}
        />
      </div>
      <div>
        <Controller
          control={control}
          name="effectiveDate"
          rules={{
            required: t("search.passenger.card.effectiveDate.required"),
          }}
          render={({ field, fieldState }) => (
            <div className="[&_svg]:max-h-4 [&_svg]:mt-1">
              <Datepicker
                value={stringToDate(field.value as string)}
                onChange={(e) => field.onChange(dateToString(e.date as Date))}
                error={fieldState.invalid}
                errorMsg={fieldState?.error?.message}
                label={t("search.passenger.card.effectiveDate.label")}
              />
            </div>
          )}
        />
      </div>
      <div>
        <Controller
          control={control}
          name="expirationDate"
          rules={{
            required: t("search.passenger.card.expirationDate.required"),
            validate: {
              minDate: (date) =>
                new Date(date) > new Date() ||
                t("search.passenger.card.expirationDate.minDate"),
            },
          }}
          render={({ field, fieldState }) => (
            <div className="[&_svg]:max-h-4 [&_svg]:mt-1">
              <Datepicker
                value={stringToDate(field.value as string)}
                onChange={(e) => field.onChange(dateToString(e.date as Date))}
                error={fieldState.invalid}
                errorMsg={fieldState?.error?.message}
                label={t("search.passenger.card.expirationDate.label")}
              />
            </div>
          )}
        />
      </div>
      <div className="col-span-2">
        <Controller
          control={control}
          name="consentCheck"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <Checkbox
              checked={field.value}
              onChange={() => field.onChange(!field.value)}
              checkmarkType="default"
              labelPlacement="right"
              error={fieldState.invalid}
            >
              <span className="text-xs">
                {t("search.passenger.card.checkboxConsent")}
              </span>
              <span> </span>
              <a
                className="text-xs underline text-cnam-blue"
                href="https://www.thetreep.com/"
              >
                {t("search.passenger.card.privacyPolicyLink")}
              </a>
            </Checkbox>
          )}
        />
      </div>
      <div className="col-span-2">
        <Controller
          control={control}
          name="privacyCheck"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <Checkbox
              checked={field.value}
              onChange={() => field.onChange(!field.value)}
              checkmarkType="default"
              labelPlacement="right"
              error={fieldState.invalid}
            >
              <span className="text-xs">
                {t("search.passenger.card.checkboxAccuracy")}
              </span>
            </Checkbox>
          )}
        />
      </div>
      <div></div>
      <div className="flex items-center justify-end gap-2">
        <Button
          size="compact"
          kind="outline"
          colors={{
            backgroundColor: theme.colors.cnam.blue.DEFAULT,
            color: theme.colors.cnam.blue.DEFAULT,
          }}
          onClick={onClose}
        >
          {t("cancel")}
        </Button>
        <Button size="compact" onClick={onFormSubmit} disabled={!isFormFilled}>
          {t("confirm")}
        </Button>
      </div>
    </div>
  );
}
